/**
 * ============================================================================
 * AstroX - Service de Gestion des Notifications Web Push (VAPID / Service Worker)
 * ============================================================================
 */

import { WebPushSubscription } from '../types';
import { getApiUrl } from './socket';

export interface PushDiagnosticResult {
    success: boolean;
    step: 'unsupported' | 'permission_denied' | 'sw_error' | 'vapid_error' | 'subscribe_error' | 'server_error' | 'success';
    message: string;
    subscription?: WebPushSubscription;
    permission?: string;
    isIos?: boolean;
    isStandalone?: boolean;
}

/**
 * Timeout helper pour éviter tout blocage infini sur Promise
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMsg: string): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error(timeoutMsg)), timeoutMs))
    ]);
}

/**
 * Détecte si l'appareil est sous iOS
 */
export function isIosDevice(): boolean {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Détecte si l'application est en mode PWA plein écran (Écran d'accueil)
 */
export function isStandaloneApp(): boolean {
    if (typeof window === 'undefined') return false;
    return (window.matchMedia('(display-mode: standalone)').matches) || 
           ((navigator as any).standalone === true);
}

/**
 * Convertit une clé VAPID base64url en Uint8Array pour le PushManager
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
    try {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    } catch (err) {
        console.error('[PUSH] Erreur conversion clé VAPID:', err);
        throw new Error('Format de clé VAPID invalide');
    }
}

/**
 * Vérifie si les notifications push et le Service Worker sont supportés par le navigateur
 */
export function isPushSupported(): boolean {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

/**
 * Récupère l'état actuel de la permission de notification
 */
export function getPushPermissionState(): NotificationPermission | 'unsupported' {
    if (!isPushSupported()) return 'unsupported';
    return Notification.permission;
}

/**
 * Enregistre le Service Worker (sw.js) de façon non-bloquante
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;

    try {
        // Vérifier si déjà enregistré
        let reg = await navigator.serviceWorker.getRegistration('/');
        if (!reg) {
            reg = await withTimeout(
                navigator.serviceWorker.register('/sw.js', { scope: '/' }),
                5000,
                'Timeout lors de l\'enregistrement du Service Worker'
            );
        }
        console.log('[PUSH] [OK] Service Worker prêt, scope:', reg?.scope);
        return reg || null;
    } catch (error) {
        console.error('[PUSH] [ERR] Échec enregistrement Service Worker:', error);
        return null;
    }
}

/**
 * Récupère la clé VAPID publique depuis le serveur
 */
export async function getVapidPublicKey(): Promise<string | null> {
    try {
        const apiUrl = getApiUrl();
        console.log('[PUSH] [REQ] Récupération clé VAPID depuis:', `${apiUrl}/api/push/vapid-public-key`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 6000);
        
        const res = await fetch(`${apiUrl}/api/push/vapid-public-key`, {
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (!res.ok) {
            console.error('[PUSH] [ERR] Erreur HTTP VAPID:', res.status, res.statusText);
            return null;
        }
        const data = await res.json();
        console.log('[PUSH] [OK] Clé VAPID reçue:', data.publicKey ? data.publicKey.substring(0, 20) + '...' : 'VIDE');
        return data.publicKey || null;
    } catch (e: any) {
        console.error('[PUSH] [ERR] Impossible de récupérer la clé VAPID:', e.message || e);
        return null;
    }
}

/**
 * Diagnostic complet & activation Push pas-à-pas avec message explicite et zéro blocage
 */
export async function activateAndDiagnosePush(clientData?: { id?: string; phone?: string; name?: string }): Promise<PushDiagnosticResult> {
    const isIos = isIosDevice();
    const isStandalone = isStandaloneApp();

    if (isIos && !isStandalone && !('Notification' in window)) {
        return {
            success: false,
            step: 'unsupported',
            isIos: true,
            isStandalone: false,
            message: "Sur iPhone / iOS : Les notifications Web Push nécessitent d'ajouter AstroX à votre écran d'accueil.\n\nCliquez sur l'icône Partager en bas de Safari puis sélectionnez 'Sur l'écran d'accueil'."
        };
    }

    if (!isPushSupported()) {
        return {
            success: false,
            step: 'unsupported',
            isIos,
            isStandalone,
            message: "Votre navigateur actuel ne supporte pas les notifications Web Push (Service Worker ou Push Manager manquant)."
        };
    }

    // Étape 1 : Demande de permission
    let permission = Notification.permission;
    if (permission === 'default') {
        try {
            permission = await withTimeout(
                Notification.requestPermission(),
                10000,
                "La demande de permission n'a pas répondu à temps."
            );
        } catch (e: any) {
            return {
                success: false,
                step: 'permission_denied',
                permission,
                message: `Erreur lors de la demande d'autorisation : ${e.message || e}`
            };
        }
    }

    if (permission !== 'granted') {
        return {
            success: false,
            step: 'permission_denied',
            permission,
            message: "L'autorisation des notifications a été refusée ou bloquée par votre navigateur.\n\nCliquez sur les options du site dans la barre d'adresse pour passer les Notifications sur 'Autoriser'."
        };
    }

    // Étape 2 : Service Worker
    let registration: ServiceWorkerRegistration | null = null;
    try {
        registration = await registerServiceWorker();
    } catch (e: any) {
        return {
            success: false,
            step: 'sw_error',
            message: `Erreur Service Worker : ${e.message || e}`
        };
    }

    if (!registration) {
        return {
            success: false,
            step: 'sw_error',
            message: "Impossible d'enregistrer le Service Worker (/sw.js). Vérifiez que vous êtes bien sur https://astrox.fr."
        };
    }

    // Étape 3 : Clé VAPID
    let publicKey: string | null = null;
    try {
        publicKey = await getVapidPublicKey();
    } catch (e: any) {
        return {
            success: false,
            step: 'vapid_error',
            message: `Erreur récupération clé VAPID : ${e.message || e}`
        };
    }

    if (!publicKey) {
        return {
            success: false,
            step: 'vapid_error',
            message: "Impossible de récupérer la clé VAPID publique depuis le serveur AstroX (/api/push/vapid-public-key)."
        };
    }

    // Étape 4 : Push Subscription
    let subscription: PushSubscription | null = null;
    try {
        subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            const convertedKey = urlBase64ToUint8Array(publicKey);
            subscription = await withTimeout(
                registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: convertedKey
                }),
                8000,
                "Délai d'attente dépassé lors de la création de l'abonnement Push sur le navigateur."
            );
        }
    } catch (e: any) {
        return {
            success: false,
            step: 'subscribe_error',
            message: `Erreur lors de la souscription PushManager : ${e.message || e}`
        };
    }

    if (!subscription) {
        return {
            success: false,
            step: 'subscribe_error',
            message: "Le gestionnaire de push du navigateur n'a pas retourné d'abonnement valide."
        };
    }

    // Étape 5 : Envoi au serveur
    const subJson = subscription.toJSON();
    const webPushSub: WebPushSubscription = {
        endpoint: subscription.endpoint,
        keys: {
            p256dh: subJson.keys?.p256dh || '',
            auth: subJson.keys?.auth || ''
        },
        userAgent: navigator.userAgent,
        subscribedAt: new Date().toISOString(),
        clientId: clientData?.id,
        phone: clientData?.phone
    };

    try {
        localStorage.setItem('astro_push_subscription', JSON.stringify(webPushSub));
        localStorage.setItem('astro_push_enabled', 'true');
    } catch {}

    const apiUrl = getApiUrl();
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        
        const response = await fetch(`${apiUrl}/api/push/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                subscription: webPushSub,
                clientId: clientData?.id,
                phone: clientData?.phone,
                name: clientData?.name
            }),
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        const result = await response.json();
        if (result.success) {
            return {
                success: true,
                step: 'success',
                subscription: webPushSub,
                message: `Notifications Push activées avec succès ! Cet appareil Android est désormais connecté à votre compte (${result.totalSubscribers} appareil(s) abonnés au total).`
            };
        } else {
            return {
                success: false,
                step: 'server_error',
                message: result.error || "Le serveur a rejeté l'enregistrement du push."
            };
        }
    } catch (err: any) {
        return {
            success: false,
            step: 'server_error',
            message: `Erreur de communication avec le serveur : ${err.message || err}`
        };
    }
}

/**
 * Souscrit aux notifications Web Push et synchronise le jeton avec le serveur
 */
export async function subscribeToPushNotifications(clientData?: { id?: string; phone?: string; name?: string }): Promise<WebPushSubscription | null> {
    const res = await activateAndDiagnosePush(clientData);
    return res.subscription || null;
}

/**
 * Demande la permission à l'utilisateur et effectue l'inscription Push automatique
 */
export async function requestPushPermissionAndSubscribe(clientData?: { id?: string; phone?: string; name?: string }): Promise<boolean> {
    const res = await activateAndDiagnosePush(clientData);
    return res.success;
}
